import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [supabaseSession, setSupabaseSession] = useState(undefined); // undefined = 初期化中
  const [localEmail, setLocalEmail] = useState(null);
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [magicLinkRequired, setMagicLinkRequired] = useState(false);

  useEffect(() => {
    // system_settings を匿名で読み込み（ログイン前でも取得できる）
    supabase.from('system_settings').select('key, value').eq('key', 'magic_link_required').then(({ data }) => {
      if (data && data.length > 0) {
        setMagicLinkRequired(data[0].value === true || data[0].value === 'true');
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupabaseSession(session);
      if (!session) {
        setLocalEmail(localStorage.getItem('pm_local_email'));
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupabaseSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Supabaseセッションがある場合のみプロファイルを取得
  useEffect(() => {
    if (!supabaseSession) {
      setProfile(null);
      setCompany(null);
      return;
    }

    supabase
      .from('user_profiles')
      .select('*, company:companies(*)')
      .eq('id', supabaseSession.user.id)
      .single()
      .then(({ data, error }) => {
        if (error || !data) return;
        setProfile(data);
        setCompany(data.company);
      });
  }, [supabaseSession]);

  // 公開するセッション: Supabaseセッション優先、なければローカルメール
  const session = supabaseSession || (localEmail ? { user: { email: localEmail, id: null } } : null);
  const loading = supabaseSession === undefined;

  const signIn = async (email) => {
    if (magicLinkRequired) {
      // マジックリンク必須モード: メールを送信してクリックを待つ
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, matchCount: 0 }),
      }).catch(() => {});
      return { error: error || null, awaitLink: true };
    } else {
      // 即時アクセスモード: ローカル保存 + マジックリンクはバックグラウンド送信
      localStorage.setItem('pm_local_email', email);
      setLocalEmail(email);
      supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      }).catch(() => {});
      fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, matchCount: 0 }),
      }).catch(() => {});
      return { error: null };
    }
  };

  const signOut = async () => {
    localStorage.removeItem('pm_local_email');
    setLocalEmail(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, profile, company, signIn, signOut, loading, magicLinkRequired }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
